/**
 * Info column: live performance and world diagnostics.
 *
 * All figures come from the simulation's own statistics plus the render loop's
 * timings, so what is displayed is what the engine measured rather than a
 * separately computed estimate.
 */

import { VIEW_MODES } from '../rendering/viewModes.ts';
import { STATE_STRIDE } from '../simulation/constants.ts';
import { formatBytes, Stat } from './controls.tsx';
import { setBackend, useRuntimeVersion, type BackendChoice, type SimulationRuntime } from '../app/runtime.ts';

/** Raster widths offered when adaptive quality is off. */
const RASTER_OPTIONS = [160, 200, 260, 320, 400];

export function InfoPanel({
  runtime,
  fps,
  renderMs,
  simMs,
  rasterWidth,
}: {
  runtime: SimulationRuntime;
  fps: number;
  renderMs: number;
  simMs: number;
  rasterWidth: number;
}) {
  useRuntimeVersion(runtime);
  const s = runtime.getStats();
  const p = runtime.getParams();
  const cells = runtime.backend.width * runtime.backend.height;
  const mode = VIEW_MODES.find((m) => m.id === runtime.mode);

  const simSeconds = s.simTime;
  const simTimeLabel =
    simSeconds < 120
      ? `${simSeconds.toFixed(0)} s`
      : simSeconds < 7200
        ? `${(simSeconds / 60).toFixed(1)} min`
        : simSeconds < 172800
          ? `${(simSeconds / 3600).toFixed(1)} h`
          : `${(simSeconds / 86400).toFixed(1)} d`;

  return (
    <div className="flex h-full flex-col overflow-y-auto">
      <section className="border-b border-rule px-3 py-2.5">
        <h2 className="mb-1.5 text-[10px] uppercase tracking-[0.14em] text-ink-faint">性能</h2>
        <Stat label="帧率" value={fps.toFixed(0)} hint="渲染循环帧率，已平滑" />
        <Stat label="帧耗时" value={`${(1000 / Math.max(1, fps)).toFixed(1)} ms`} />
        <Stat label="渲染" value={`${renderMs.toFixed(1)} ms`} hint="3D 视口光栅化耗时" />
        <Stat
          label="光栅"
          value={`${rasterWidth} px`}
          hint={runtime.backend.kind === 'gpu'
            ? 'WebGPU 按设备像素比直接输出，最高 2×；不再放大低分辨率软件画面'
            : `软件光线步进的内部分辨率；画布以 ${rasterWidth} px 光栅化后由浏览器放大到显示尺寸。面积越大越清晰，耗时按面积增长`}
        />
        <div className={`${runtime.backend.kind === 'gpu' ? 'hidden' : 'flex'} items-baseline justify-between gap-3 py-[2px]`}>
          <span className="text-[11px] text-ink-muted">光栅宽度</span>
          <select
            value={runtime.renderWidth}
            onChange={(e) => {
              runtime.renderWidth = Number(e.target.value);
              runtime.touch();
            }}
            title="内部分辨率，立即生效。手动调节：运行时自动改分辨率会让整个画面缩放一下，很晃眼"
            className="max-w-[132px] appearance-none border border-rule bg-paper-raised px-1 py-[1px] font-mono text-[10px] text-ink outline-none focus:border-rule-strong"
          >
            {RASTER_OPTIONS.map((w) => (
              <option key={w} value={w}>
                {w} px
              </option>
            ))}
          </select>
        </div>
        <Stat label="模拟" value={`${simMs.toFixed(2)} ms`} hint="上一帧推进的所有 tick 总耗时" />
        <div className="flex items-baseline justify-between gap-3 py-[2px]">
          <span className="text-[11px] text-ink-muted">后端</span>
          <select
            value={runtime.requestedBackend}
            onChange={(e) => {
              void setBackend(runtime, e.target.value as BackendChoice);
            }}
            title="切换模拟计算的后端；切换时会保留当前地形"
            className="max-w-[132px] appearance-none border border-rule bg-paper-raised px-1 py-[1px] font-mono text-[10px] text-ink outline-none focus:border-rule-strong"
          >
            <option value="auto">自动</option>
            <option value="gpu">GPU 计算</option>
            <option value="cpu">CPU 计算</option>
          </select>
        </div>
        <Stat
          label="设备"
          value={
            runtime.backend.kind === 'gpu'
              ? runtime.adapterName || '初始化中…'
              : runtime.requestedBackend === 'gpu'
                ? runtime.backendError || 'GPU 不可用，已回退 CPU'
                : 'CPU（类型化数组）'
          }
          hint="实际运行模拟的适配器"
        />
      </section>

      <section className="border-b border-rule px-3 py-2.5">
        <h2 className="mb-1.5 text-[10px] uppercase tracking-[0.14em] text-ink-faint">世界</h2>
        <Stat label="Tick 数" value={s.tick.toLocaleString()} />
        <Stat label="模拟时间" value={simTimeLabel} hint="自上次重置以来累计推进的模拟时间" />
        <Stat
          label="网格"
          value={`${runtime.backend.width} × ${runtime.backend.height}`}
          hint={`${cells.toLocaleString()} cells`}
        />
        <Stat
          label="网格尺寸"
          value={`${p.terrain.cellSize} m`}
          hint={`World extent ${((p.terrain.resolution * p.terrain.cellSize) / 1000).toFixed(1)} km`}
        />
        <Stat label="状态内存" value={formatBytes(cells * STATE_STRIDE * 4 * 2)} hint="两个打包状态缓冲区（ping-pong 乒乓）" />
      </section>

      <section className="border-b border-rule px-3 py-2.5">
        <h2 className="mb-1.5 text-[10px] uppercase tracking-[0.14em] text-ink-faint">地形</h2>
        <Stat label="最高" value={`${s.maxHeight.toFixed(0)} m`} />
        <Stat label="最低" value={`${s.minHeight.toFixed(0)} m`} />
        <Stat label="平均" value={`${s.meanHeight.toFixed(0)} m`} />
      </section>

      <section className="border-b border-rule px-3 py-2.5">
        <h2 className="mb-1.5 text-[10px] uppercase tracking-[0.14em] text-ink-faint">物质</h2>
        <Stat
          label="水量"
          value={`${(s.waterTotal * p.terrain.cellSize ** 2 / 1e6).toFixed(2)} Mm³`}
          hint="整个网格上的地表水与地下水储量总和"
        />
        <Stat
          label="悬移质"
          value={`${(s.sedimentTotal * p.terrain.cellSize ** 2 / 1e6).toFixed(3)} Mm³`}
          hint="水流当前携带的悬移质总量"
        />
        <Stat
          label="风化层"
          value={`${(s.looseTotal * p.terrain.cellSize ** 2 / 1e6).toFixed(2)} Mm³`}
          hint="可被风和水搬运的松散物质总量"
        />
        <Stat
          label="风蚀外输"
          value={`${(s.windLossTotal * p.terrain.cellSize ** 2 / 1e6).toFixed(3)} Mm³`}
          hint="被风带出计算域、不再返回的物质"
        />
      </section>

      <section className="px-3 py-2.5">
        <h2 className="mb-1.5 text-[10px] uppercase tracking-[0.14em] text-ink-faint">显示模式</h2>
        <p className="text-[11px] leading-snug text-ink-muted">{mode?.hint ?? '—'}</p>
      </section>
    </div>
  );
}
